// R-131-FIN-DECISION — named, documented constants for pathway decision ranking
// and recommendation heuristics. Extracted from decisionRecommendation.ts so the
// magic numbers that drive rankings/confidence are auditable in one place.
//
// SOURCING NOTE: The thresholds below are INTERNAL HEURISTICS, not externally
// calibrated. Where an industry standard exists it is cited; otherwise the
// constant is explicitly marked as an internal heuristic. Do not "tune" these
// without recording the rationale — they shift report rankings and the
// confidence rating shown to users.

import { DEFAULT_CARBON_PRICE_USD_PER_T } from './pathwayCarbonAdjust';

/**
 * Carbon penalty applied per tCO2e/yr when a binding carbon commitment is in
 * effect, used ONLY for *ranking* pathways in `rankPathways`.
 *
 * CORRECTED (carbon-penalty sign-off): this previously defaulted to
 * 50,000 USD/tCO2e — exactly 1000x the documented shadow carbon price
 * `DEFAULT_CARBON_PRICE_USD_PER_T = 50` USD/tCO2e used everywhere else for
 * actual carbon cost economics (see `pathwayCarbonAdjust.ts`). That 1000x
 * inflation made the ranking carbon term dominate NPV, so under any binding
 * carbon commitment a zero-emission pathway won regardless of NPV. It was a
 * units bug ($50/tCO2e accidentally scaled by 1000).
 *
 * The default is now the documented shadow carbon price. We REFERENCE
 * `DEFAULT_CARBON_PRICE_USD_PER_T` directly (rather than re-typing 50) so the
 * ranking penalty and the carbon-cost economics share a SINGLE SOURCE OF TRUTH
 * and cannot drift apart. Callers can still override the ranking penalty per
 * tCO2e via `opts.carbonPricePerTUsd` in `recommendPathwayComparison`.
 *
 * IMPACT: pathway rankings shift under a binding carbon commitment. Carbon is
 * now weighted at the real shadow price ($50/tCO2e), so it nudges — rather than
 * dominates — the NPV-based ranking. Emitting pathways are no longer forced
 * below a zero-emission one purely on the carbon term.
 *
 * Units: USD per (tCO2e/yr). Multiplied by `annualTCo2e` (tCO2e/yr) to produce a
 * USD/yr-scaled penalty subtracted from the NPV-denominated ranking score.
 * (NOTE — separate, out-of-scope dimensional issue: the penalty is in $/yr
 * while NPV is a lifetime $ figure, so the two are not strictly commensurate.
 * Surfaced in flagsForUser; the in-scope fix here is the value correction only.)
 */
export const CARBON_PENALTY_RANKING_USD_PER_TONNE = DEFAULT_CARBON_PRICE_USD_PER_T;

/**
 * Tie band for NPV-based ranking: if two pathways' |NPV| differ by less than
 * this fraction of the larger |NPV|, treat them as a tie and break on
 * time-to-power instead of NPV.
 *
 * 10% — internal heuristic, not externally calibrated. Chosen so pathways that
 * are economically near-indistinguishable (within Monte-Carlo-scale noise) are
 * ranked on speed-to-power rather than spurious NPV precision.
 */
export const NPV_TIE_BAND_FRACTION = 0.1;

/**
 * Confidence thresholds on the relative NPV gap between the leader and runner-up
 * (gap / max(|leaderNPV|, 1)). gap >= HIGH => "high"; >= MEDIUM => "medium";
 * else "low".
 *
 * 20% / 8% — internal heuristic, not externally calibrated. A 20%+ NPV lead is
 * treated as a clear separation; an 8–20% lead is "medium"; below 8% the leader
 * is essentially tied with the runner-up.
 */
export const CONFIDENCE_NPV_GAP_HIGH = 0.2;
export const CONFIDENCE_NPV_GAP_MEDIUM = 0.08;

/**
 * Monte-Carlo NPV band-width thresholds used to DOWNGRADE confidence based on
 * the top pathway's relative uncertainty: relWidth = (npvP90 - npvP10) / |npvUsd|.
 * relWidth <= HIGH => band-implied confidence "high"; <= MEDIUM => "medium";
 * else "low". The final confidence is the MORE CONSERVATIVE of the gap-based and
 * band-based ratings.
 *
 * These intentionally mirror the gap thresholds' spirit but are applied to the
 * P10–P90 spread. Internal heuristic, not externally calibrated.
 *   - relWidth <= 0.25 (P10–P90 within ±~12.5% of central NPV): tight band.
 *   - relWidth <= 0.60: moderate band.
 *   - relWidth  > 0.60: wide band — uncertainty dominates the leader/runner-up gap.
 */
export const CONFIDENCE_NPV_BAND_WIDTH_HIGH = 0.25;
export const CONFIDENCE_NPV_BAND_WIDTH_MEDIUM = 0.6;

/**
 * Monte-Carlo sensitivity callout thresholds in `monteCarloSensitivity`.
 * Internal heuristics, not externally calibrated.
 *   - LCOE: flag when the P10–P90 LCOE spread exceeds 15% of the central LCOE.
 *   - NPV:  flag when the |P10–P90| NPV spread exceeds 25% of the central |NPV|.
 */
export const SENSITIVITY_LCOE_SPREAD_FRACTION = 0.15;
export const SENSITIVITY_NPV_SPREAD_FRACTION = 0.25;

/**
 * Correlated-Monte-Carlo win-probability thresholds (R-131-FIN-DECISION-PWIN).
 *
 * The leader's pWinNpv from `runComparisonMonteCarlo` (the fraction of
 * correlated-shock iterations in which the leader has the HIGHEST NPV) is
 * folded into the confidence rating as a THIRD signal, combined via the
 * existing "more conservative wins" rule. A leader that wins only ~55% of
 * correlated runs is fragile even with a wide deterministic NPV gap, because
 * a single common-mode shock (fuel/queue/CAPEX) flips the ranking.
 *
 *   pWin >= HIGH   => win-based confidence "high"  (leader wins most futures)
 *   pWin >= MEDIUM => "medium"
 *   pWin  < MEDIUM => "low"   (the leader loses too often to be trusted)
 *
 * 0.80 / 0.60 — internal heuristic, not externally calibrated. Spirit: an
 * 80%+ correlated win-rate is a robust leader; 60–80% is contestable;
 * below 60% (e.g. a coin-flip 55%) the "winner" is not decision-grade.
 * Do not tune without recording the rationale — this shifts the user-facing
 * confidence rating.
 */
export const CONFIDENCE_PWIN_HIGH = 0.8;
export const CONFIDENCE_PWIN_MEDIUM = 0.6;
